import { createAsyncThunk } from "@reduxjs/toolkit";
import { APP_PATH } from "../../api/backendPath";
import { toast } from '../../components/toast';
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

import i18n from "../../i18n";
// Fetch nurse packages
export const fetchNursePackagesReducer = createAsyncThunk(
  "packages/fetchNursePackages",
  async ({ status = "", search = "", page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    const axiosPrivate = await useAxiosPrivate();
    try {
      const queryParamsObj = { page: String(page), limit: String(limit) };
      if (status && status !== "All") queryParamsObj.status = status;
      if (search) queryParamsObj.search = search;
      const queryParams = new URLSearchParams(queryParamsObj).toString();
      const fullUrl = `${process.env.REACT_APP_API_URL}${APP_PATH.nursePackages}?${queryParams}`;
      const response = await axiosPrivate.get(fullUrl);
      if (response?.data?.status === true || response?.status === 200) {
        return response.data.data;
      }
      return rejectWithValue(response?.data?.message || i18n.t("toast.failedToFetchPackages"));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || i18n.t("toast.errorFetchingPackages");
      return rejectWithValue(errMsg);
    }
  }
);

// Create a nurse package
export const createNursePackageReducer = createAsyncThunk(
  "packages/createNursePackage",
  async (payload, { rejectWithValue }) => {
    const axiosPrivate = await useAxiosPrivate();
    try {
      const fullUrl = `${process.env.REACT_APP_API_URL}${APP_PATH.nursePackages}`;
      const response = await axiosPrivate.post(fullUrl, payload);
      if (response?.data?.status === true || response?.status === 201 || response?.status === 200) {
        toast.success(response?.data?.message || i18n.t("toast.packageAddedSuccessfully"));
        return response.data.data;
      }
      const msg = response?.data?.message || i18n.t("toast.failedToAddPackage");
      toast.error(msg);
      return rejectWithValue(msg);
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || i18n.t("toast.errorAddingThePackage");
      toast.error(errMsg);
      return rejectWithValue(errMsg);
    }
  }
);

// Update a nurse package
export const updateNursePackageReducer = createAsyncThunk(
  "packages/updateNursePackage",
  async ({ id, ...payload }, { rejectWithValue }) => {
    const axiosPrivate = await useAxiosPrivate();
    try {
      const fullUrl = `${process.env.REACT_APP_API_URL}${APP_PATH.nursePackages}/${id}`;
      const response = await axiosPrivate.put(fullUrl, payload);
      if (response?.data?.status === true || response?.status === 200) {
        // No success toast here: the backend emits a real-time notification
        // ("Your package '...' has been updated successfully") which is shown
        // as the single confirmation toast. A local toast would duplicate it.
        return response.data.data;
      }
      const msg = response?.data?.message || i18n.t("toast.failedToUpdatePackage");
      toast.error(msg);
      return rejectWithValue(msg);
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || i18n.t("toast.errorUpdatingThePackage");
      toast.error(errMsg);
      return rejectWithValue(errMsg);
    }
  }
);

// Delete a nurse package
export const deleteNursePackageReducer = createAsyncThunk(
  "packages/deleteNursePackage",
  async ({ id }, { rejectWithValue }) => {
    const axiosPrivate = await useAxiosPrivate();
    try {
      const fullUrl = `${process.env.REACT_APP_API_URL}${APP_PATH.nursePackages}/${id}`;
      const response = await axiosPrivate.delete(fullUrl);
      if (response?.data?.status === true || response?.status === 200) {
        toast.success(response?.data?.message || i18n.t("toast.packageRemovedSuccessfully"));
        return response.data.data;
      }
      const msg = response?.data?.message || i18n.t("toast.failedToRemovePackage");
      toast.error(msg);
      return rejectWithValue(msg);
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || i18n.t("toast.errorRemovingThePackage");
      toast.error(errMsg);
      return rejectWithValue(errMsg);
    }
  }
);
